// shared/src/news/news-impact-store.ts
// D1 store for the derived News Impact table (migrations/0075_news_impact.sql).
// Writer (finalize + backfill) and reader (api-worker endpoint) share this one
// module so the INSERT/SELECT column list lives in a single place (mirrors the
// news_processing_log write-log.ts pattern). See
// docs/news-impact-derived-2026-07-08-design.md.

import { z } from 'zod';
import type { NewsImpactItem, NewsImpactStage, NewsImpactSource } from './derive-impact.js';

// Structural D1 subset (prepare → bind → batch/all) so shared does not depend on
// @cloudflare/workers-types (same pattern as runtime-params.ts / write-log.ts).
export interface NewsImpactD1 {
  prepare(sql: string): NewsImpactD1Stmt;
  batch(statements: NewsImpactD1Stmt[]): Promise<unknown[]>;
}
export interface NewsImpactD1Stmt {
  bind(...values: unknown[]): NewsImpactD1Stmt;
  all<T = unknown>(): Promise<{ results: T[]; success: boolean }>;
  run(): Promise<unknown>;
}

/** The raw DB row (cited as 0/1 INTEGER). Mirrors the 0075 DDL columns. */
export interface NewsImpactRow {
  readonly id:         string;
  readonly brief_id:   string;
  readonly stage:      string;
  readonly ticker:     string | null;
  readonly source:     string;
  readonly category:   string;
  readonly title:      string;
  readonly item_date:  string;
  readonly summary:    string;
  readonly cited:      number;   // 0 | 1
  readonly score:      number;   // 0 | 1 | 2
  readonly created_at: string;
}

/** Zod schema for the DB row → used at the api boundary to validate reads
 *  before they cross into the wire response (C035). */
export const NewsImpactRowSchema = z.object({
  id:         z.string(),
  brief_id:   z.string(),
  stage:      z.enum(['M', 'P']),
  ticker:     z.string().nullable(),
  source:     z.enum(['rss', 'macro_gdelt', 'ticker']),
  category:   z.string(),
  title:      z.string(),
  item_date:  z.string(),
  summary:    z.string(),
  cited:      z.number().int().min(0).max(1),
  score:      z.number().int().min(0).max(2),
  created_at: z.string(),
});

const COLS = 'id, brief_id, stage, ticker, source, category, title, item_date, summary, cited, score, created_at';
const INSERT_SQL = `INSERT OR REPLACE INTO news_impact (${COLS}) VALUES (?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?)`;

function itemToRow(item: NewsImpactItem, createdAtIso: string): NewsImpactRow {
  return {
    id: item.id, brief_id: item.brief_id, stage: item.stage, ticker: item.ticker,
    source: item.source, category: item.category, title: item.title,
    item_date: item.item_date, summary: item.summary,
    cited: item.cited ? 1 : 0, score: item.score, created_at: createdAtIso,
  };
}

/** Row → wire item (cited back to boolean). */
export function rowToItem(r: NewsImpactRow): NewsImpactItem {
  return {
    id: r.id, brief_id: r.brief_id,
    stage: (r.stage === 'P' ? 'P' : 'M') as NewsImpactStage,
    ticker: r.ticker,
    source: r.source as NewsImpactSource,
    category: r.category, title: r.title, item_date: r.item_date, summary: r.summary,
    cited: r.cited === 1,
    score: (r.score === 2 ? 2 : r.score === 1 ? 1 : 0),
  };
}

/**
 * Replace a brief's news_impact rows with the freshly-derived set: DELETE the
 * brief's rows then batch-INSERT. Idempotent (deterministic ids + full replace),
 * so a re-derive (finalize retry / backfill re-run) converges exactly. No-op
 * with a single DELETE when items is empty (a brief with no news still clears
 * any stale rows).
 */
export async function replaceNewsImpactForBrief(
  db:           NewsImpactD1,
  briefId:      string,
  items:        ReadonlyArray<NewsImpactItem>,
  createdAtIso: string,
): Promise<void> {
  const del = db.prepare('DELETE FROM news_impact WHERE brief_id = ?').bind(briefId);
  if (items.length === 0) { await del.run(); return; }
  const stmt = db.prepare(INSERT_SQL);
  const inserts = items.map((it) => {
    const r = itemToRow(it, createdAtIso);
    return stmt.bind(r.id, r.brief_id, r.stage, r.ticker, r.source, r.category, r.title, r.item_date, r.summary, r.cited, r.score, r.created_at);
  });
  await db.batch([del, ...inserts]);
}

/** Read all news_impact rows for one brief (the tab/drawer source). */
export async function readNewsImpactForBrief(
  db:      NewsImpactD1,
  briefId: string,
): Promise<NewsImpactRow[]> {
  const res = await db
    .prepare(`SELECT ${COLS} FROM news_impact WHERE brief_id = ? ORDER BY score DESC, title ASC`)
    .bind(briefId)
    .all<NewsImpactRow>();
  return res.results ?? [];
}
